import { getCookie } from '@/utils/general/cookie'
import type { ILoyaltyConstraint, ILoyaltyUhpContentMeta } from '@/types/unifiedExpandWidgets'

// analytics fallback used across every loyalty raven attribute
const NA = 'na'

const getUtmSource = (): string => {
    if (typeof window !== 'undefined') {
        const parts = window.location?.search?.split('utm_source=')
        if (parts && parts.length > 1) {
            return parts.pop()?.split('&')?.[0] || getCookie('utm_source') || 'organic'
        }
    }
    return getCookie('utm_source') || 'organic'
}

const getCountConstraint = (
    meta: ILoyaltyUhpContentMeta | null | undefined
): ILoyaltyConstraint | undefined => {
    const list = meta?.constraintList
    return list?.find(c => c.type === 'COUNT') ?? list?.[0]
}

const deriveEliteProgress = (meta: ILoyaltyUhpContentMeta | null | undefined): string => {
    const constraint = getCountConstraint(meta)
    if (!constraint || constraint.target == null) {
        return NA
    }
    return `${constraint.bookingCount ?? 0}/${constraint.target}`
}

const deriveEliteStatus = (meta: ILoyaltyUhpContentMeta | null | undefined): string => {
    if (meta?.loyaltyStatus?.toUpperCase() === 'SUSPENDED') {
        return 'suspended'
    }
    switch (meta?.displayStatus?.toUpperCase()) {
        case 'ACTIVE':
            return 'active'
        case 'INACTIVE':
            return 'inactive'
        case 'EXPIRING_SOON':
        case 'TRACKER_EXPIRING_SOON':
            return 'expiring_soon'
        case 'ACTIVATING_SOON':
            return 'activating_soon'
        case 'EXPIRED':
            return 'expired'
        default:
            // no displayStatus: fall back to the raw loyaltyStatus, else na
            return meta?.loyaltyStatus?.toLowerCase() ?? NA
    }
}

// Shared loyalty raven attributes for both the FHP elite pill (header) and the elite banner.
export const getLoyaltyRavenAttributes = (
    meta: ILoyaltyUhpContentMeta | null | undefined
): Record<string, string> => ({
    elite_status: deriveEliteStatus(meta),
    elite_progress: deriveEliteProgress(meta),
    partner_program_source: meta?.partnerProgram?.toLowerCase() ?? 'none',
    elite_unlock_date: meta?.allotmentDate ?? NA,
    elite_membership_end_date: meta?.viewExpiryDate ?? NA,
    utm_source: getUtmSource()
})
